// app/src/features/timeline/useTimelineGeometry.js
// Moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1; reads props and selectors, never the store.
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useMinuteTick } from "../../hooks/useMinuteTick.js";
import { useQuestionClearance } from "../../hooks/useQuestionClearance.js";
import { useTimelineZoom } from "../../hooks/useTimelineZoom.js";
import { SIGNAL_BAR_H } from "../../lib/drawerStack.js";
import { premiseViewsFor } from "../../lib/forecast/branchPremise.js";
import { eventKey } from "../../lib/forecast/eventIdentity.js";
import { awaitingAnswer, closeMs } from "../../lib/forecast/answerCheck.js";
import { useAnswerClock } from "../resolution/useAnswerClock.js";
import { ANSWER_BOX_GAP, ANSWER_BOX_W } from "../resolution/answerBox.js";
import { extractDistribution, isNamedOutcomeVerdict } from "../../lib/forecast/multiway.js";
import { resolvedFrameTarget } from "../../lib/forecast/resolvedFrame.js";
import {
  projectRunForLane,
  projectionDateAtLocalNoon,
  projectionDayKey,
  refreshLanePastFlags,
  selectActiveRun,
  selectComparisonLanes,
  selectPremiseRunsById,
  selectRun,
  selectScenarioRuns,
  selectVisibleBranchRuns,
} from "../../lib/forecast/selectors.js";
import { withLiveIdentity } from "../../lib/forecast/vintageContract.js";
import { parseResolveDateLocal, resolutionMomentLocal } from "../../lib/formatDates.js";
import { orderBranchesByFork, stackedLaneLayout, unionDateRange } from "../../lib/laneLayout";
import {
  AXIS_H,
  BASE_R,
  BRANCH_SPACING,
  R_END,
  R_START,
  SCALE_PRESETS,
  clientToLocalX,
  getFineTicks,
  getTicks,
} from "../../lib/timeline/geometry.js";
import { LANE_SPINE_Y } from "../../lib/timeline/laneBand.js";
import { resolutionRightBufferFor } from "../../lib/timeline/multiwayMarker.js";
import { resolvedMarkOf } from "../../lib/timeline/resolvedOutcome.js";
import { useBranchLinkFocus } from "./useBranchLinkFocus.js";
import {
  awaitsDetail,
  getDetailFailed,
  subscribeDetailFailed,
} from "../../lib/forecast/detailBackfill.js";

export const TODAY = new Date();
// Re-stamps each event's `isPast` relative to a GIVEN moment (a past vintage's generation time)
// instead of "now", so a rewound timeline shows occurred (grey) vs pending (pie) exactly as it did
// at that moment.
function stampEventsIsPastAsOf(events, iso) {
  const t = new Date(iso).getTime();
  // Always returns clones, so the stored snapshot stays isolated even if later code mutates event
  // objects.
  return (events || []).map((e) => {
    const end = new Date(e.date_end || e.date).getTime();
    return !isNaN(t) && !isNaN(end) ? { ...e, isPast: end <= t } : { ...e };
  });
}

/**
 * Does any lane have a scenario whose premise the clock can move? Cleared scenarios count: they
 * still mark their triggers on the timeline (withScenarioOutcomes).
 */
export function premiseClockNeeded(laneRuns) {
  return laneRuns.some((lane) => (lane.scenarios || lane.branches || []).length > 0);
}

/**
 * The lane runs each lane renders (the live projection of the active run and its branches, or the
 * compare lanes) and the branch premise views, memoized on the store state and the displayed clock.
 */
function useLaneRuns({
  active,
  applyAlternateSpace,
  branches,
  branchesAt,
  compareOn,
  debugPremiseOverride,
  forecastLiveProgressEnabled,
  runState,
  timelineToday,
  viewMomentIso,
  viewSnap,
}) {
  // Memoized on [runState, compareOn]: AppInner re-renders on every hover / drawer toggle /
  // animation frame, and without the memo each would rebuild the parent→children index + DFS per
  // lane (selectRunTree) while runState is unchanged.
  //
  // Live trigger `isPast` is a view fact, so it must use the same displayed clock as the today line
  // (including mock-today and historical rewind), not ambient wall time. The projection is
  // day-granular; this scalar keeps the memo stable within a day.
  const liveProjectionDayKey = projectionDayKey(timelineToday);
  const liveProjectionToday = useMemo(
    () => projectionDateAtLocalNoon(liveProjectionDayKey),
    [liveProjectionDayKey],
  );
  const projectedLaneRuns = useMemo(() => {
    const roots = (() => {
      if (compareOn) {
        const seen = new Set();
        return selectComparisonLanes(runState).filter((r) => {
          const k = r.forecastId || r.runId;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      }
      const a = selectActiveRun(runState);
      return a ? [a] : [];
    })();
    const mapped = roots.map((rawRoot) => ({
      root: projectRunForLane(rawRoot, liveProjectionToday, forecastLiveProgressEnabled),
      // The SAME branch list the lane geometry is built from (laneGeom) — so the render and
      // its branchYs index the same branches: live, the uncleared ones; at a past mark (single
      // mode), the ones that existed then.
      branches: (compareOn
        ? orderBranchesByFork(selectVisibleBranchRuns(runState, rawRoot.runId))
        : branches
      ).map((branch) =>
        projectRunForLane(branch, liveProjectionToday, forecastLiveProgressEnabled),
      ),
      // Every scenario in the tree, cleared ones included: the timeline's "?" marks read
      // them (withScenarioOutcomes), and clearing a scenario does not settle its trigger. At a
      // past mark (single mode), only those that existed then, like `branches`.
      scenarios: selectScenarioRuns(runState, rawRoot.runId, compareOn ? null : branchesAt),
    }));
    // Branches are deliberately not rewound: they belong to the current state.
    if (!compareOn && viewMomentIso && mapped[0]) {
      const a = mapped[0].root;
      // Use the hook-resolved snapshot (manual inline OR async fetched) so the
      // lane's triggers/events/pie rewind for BOTH kinds — not just manual.
      const snap = viewSnap;
      // report / serverExplanation / serverContext come straight from the snapshot
      // (a stored null is meaningful — see displayActive) rather than coalescing to
      // the live value.
      if (snap)
        mapped[0] = {
          root: {
            ...a,
            // Overlay the vintage's contract (probability etc.) but PIN the
            // resolution horizon to the live/canonical `resolution_date`. The
            // server may re-word — and re-date — the question on each run (see the
            // contract-key invariant), so an earlier vintage's snapshot can carry a
            // DIFFERENT resolution_date than the live contract. The resolution pie
            // is positioned from `root.contract.resolution_date`, while the vertical
            // FORECAST line is positioned from the live `active.contract.resolution_date`;
            // if they diverge, scrubbing strands the pie off the forecast line ("way
            // to the right"). The resolution horizon is a stable property of the
            // contract, so keep it canonical while probability/events/report rewind.
            // Identity fields a vintage lacks (a pre-parity scheduled fire carries only its
            // distribution) fall back to the live contract — see withLiveIdentity.
            contract: snap.contract
              ? {
                  ...withLiveIdentity(snap.contract, a.contract),
                  resolution_date: a.contract?.resolution_date ?? snap.contract.resolution_date,
                }
              : a.contract,
            // Re-stamp isPast relative to the DISPLAYED snapshot's own moment
            // (snap.generatedAt — falls back to viewMomentIso), not now, so a trigger
            // that was still PENDING then renders as a pie and only those occurred-by-then
            // render grey — a true "as of then" view (clones snap.events). Keying off the
            // snapshot's moment keeps the stamp consistent with the events being shown even
            // mid async-load (when viewMomentIso may be ahead of the held snapshot).
            events: snap.events
              ? stampEventsIsPastAsOf(snap.events, snap.generatedAt ?? viewMomentIso)
              : a.events,
            report: snap.report,
            serverExplanation: snap.serverExplanation,
            serverContext: snap.serverContext,
          },
          branches: mapped[0].branches,
          scenarios: mapped[0].scenarios,
        };
    }
    if (!compareOn && mapped[0])
      mapped[0] = { ...mapped[0], root: applyAlternateSpace(mapped[0].root) };
    return mapped;
  }, [
    runState,
    compareOn,
    branches,
    branchesAt,
    viewMomentIso,
    viewSnap,
    liveProjectionToday,
    forecastLiveProgressEnabled,
    applyAlternateSpace,
  ]);
  // Stored `isPast` re-checked against the displayed moment, minute-quantized (as
  // premiseMinuteKey below) so a hover or animation frame does not rebuild the lanes.
  const freshPastMinuteKey = Math.floor(new Date(timelineToday).getTime() / 60000);
  const laneRuns = useMemo(
    () => refreshLanePastFlags(projectedLaneRuns, new Date(freshPastMinuteKey * 60000)),
    [projectedLaneRuns, freshPastMinuteKey],
  );
  // Branch PREMISE state per branch runId — is each fork's assertion confirmed,
  // contradicted, still being watched, or not yet in its window? Pure read: for a
  // minted (contract-grade) trigger the verdict is already a row in this user's own
  // store, so this needs no request. See lib/forecast/branchPremise.ts.
  //
  // Keyed on [runState, timelineToday] — the verdict lives in the store and the
  // asymmetric-window rule is clock-dependent, so a debug today-picker move must
  // recompute it. Flattened across every lane (compare mode included) because the
  // map is keyed by runId, which is unique tree-wide.
  // Quantized to the MINUTE, not `timelineToday` itself: `effectiveToday` is a fresh
  // `new Date()` every render, so a memo keyed on its raw ms would rebuild this map (and
  // hand ForecastLane a new prop) on every hover and animation frame. A minute is fine
  // enough for a window that closes at an explicit time (a timestamped date_end /
  // resolution_time) to settle when it closes rather than at the next midnight, and a
  // date-only window ends at end-of-day, so any moment that day judges it the same.
  // useMinuteTick keeps the clock moving while the page sits idle — only while some lane
  // (compare lanes included) has scenarios to judge.
  useMinuteTick(premiseClockNeeded(laneRuns));
  const premiseMinuteKey = Math.floor(new Date(timelineToday).getTime() / 60000);
  const premiseNowMs = premiseMinuteKey * 60000;
  const premiseViews = useMemo(
    () =>
      premiseViewsFor({
        // Every scenario, not just the drawn ones, so a cleared scenario still marks its
        // trigger on the timeline.
        branchRuns: laneRuns.flatMap((l) => l.scenarios || l.branches || []),
        // Both slices: a shared forecast's branches find their parents and minted
        // evidence in sharedRunsById.
        runsById: runState ? selectPremiseRunsById(runState) : undefined,
        nowMs: premiseNowMs,
        rootRunId: active?.id ?? null,
        override: debugPremiseOverride,
      }),
    [laneRuns, runState, premiseNowMs, active?.id, debugPremiseOverride],
  );
  return { laneRuns, liveProjectionToday, premiseViews };
}

/**
 * The timeline's pointer/measure refs, view state and geometry memos, as they sat in AppInner: the
 * x-scale (ppd/viewLeft), today/generated/resolution x positions, ticks, lanes, branches and the
 * resolution marker inputs. Returns every binding the rest of the app and the timeline modules read.
 */
export function useTimelineGeometry(inputs) {
  const {
    active,
    allMultiwayEnabled,
    applyAlternateSpace,
    compareMode,
    comparison,
    currentResolutionEntry,
    debugPremiseOverride,
    debugResolutionOverride,
    displayActive,
    forecastHeaderH,
    forecastLiveProgressEnabled,
    forecastTracking,
    forecasts,
    mockToday,
    railTimelineDrop,
    runState,
    slideCache,
    themeRaw,
    viewEntry,
    viewMomentIso,
    viewSnap,
  } = inputs;

  // X-axis hover tracking — used by the Forecast Tracking date-row ticks so
  // the white "forecast was made" marker brightens when the user's cursor
  // sits at its horizontal position. Set to the layout-relative X (px from
  // the timeline container's left edge) on any hover inside the timeline
  // (it's driven by the container's onMouseMove, not just the axis strip);
  // null when the cursor leaves the timeline.
  const [axisHoverX, setAxisHoverX] = useState(null);
  // Forward ref to autoZoom() for the same reason: reattachToForecast is a
  // long-lived useCallback([]) and must not capture the mount-time autoZoom
  // (whose closure depends on chatOpen/logOpen/todayDay). Assigned during render
  // below (autoZoomRef.current=autoZoom) so a log-click/reload reattach zooms
  // against CURRENT panel state and is populated before any effect runs.
  const autoZoomRef = useRef(null);
  const [selEvt, setSelEvt] = useState(null);
  const [crossX, setCrossX] = useState(null);
  const [selRect, setSelRect] = useState(null);
  const [cSize, setCSize] = useState({ w: 800, h: 400 });
  // Becomes true once the ResizeObserver effect has populated cSize with a
  // real container measurement. The cSize state is initialized to a
  // {w:800,h:400} placeholder so other code paths can render against a
  // sensible fallback before measurement; downstream effects that need to
  // wait for a *real* width should gate on `measured`, not `cSize.w` (which
  // is always truthy thanks to the placeholder).
  const [measured, setMeasured] = useState(false);
  // Live mirror of cSize. autoZoom and friends can be invoked through long-lived
  // closures captured early — most notably the onboarding tour, whose
  // submit→doForecast→postSubmit→autoZoom chain is frozen at tour-start, when
  // cSize is still the {w:800} placeholder. Reading the ref (updated every
  // render, below) instead of the closed-over cSize.w means the zoom always
  // frames against the REAL measured width, so the canned tour forecast isn't
  // crammed into the left third of an under-zoomed timeline.
  const cSizeRef = useRef(cSize);
  cSizeRef.current = cSize;
  const panAnimRef = useRef(null);
  const vPanAnimRef = useRef(null);
  const evtScrollRef = useRef(null);
  const ppdRef = useRef(4);
  const vlRef = useRef(0);
  const [, setTick] = useState(0);
  const rafRef = useRef(null);
  const animRef = useRef(null);
  const containerRef = useRef(null);
  // `containerReady` is the *real* signal that the timeline container has
  // mounted into the DOM. The ResizeObserver effect below used to depend on
  // [authLoading, me] as a proxy for "main app rendered" — but that proxy
  // fails when the user is held on the ToS gate after auth resolves: the
  // main app (and thus the container) isn't rendered yet, the effect runs
  // once with containerRef.current null, and isn't re-triggered when the
  // gate dismisses (only `tos` changes, which wasn't in the deps). The
  // callback ref below flips this flag exactly when the element mounts /
  // unmounts, so the observer attaches reliably regardless of which gate
  // is active.
  const [containerReady, setContainerReady] = useState(false);
  const setContainerRef = useCallback((el) => {
    containerRef.current = el;
    setContainerReady(!!el);
  }, []);
  const dragRef = useRef(null);
  const selectRef = useRef(null);

  const ppd = ppdRef.current;
  const viewLeft = vlRef.current;

  const dayToScreen = useCallback((d) => {
    const dayNum = (new Date(d) - R_START) / 86400000;
    return (dayNum - vlRef.current) * ppdRef.current;
  }, []); // refs are always current

  const screenToDay = useCallback((sx) => {
    return vlRef.current + sx / ppdRef.current;
  }, []); // refs are always current

  const scheduleRender = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setTick((t) => t + 1);
    });
  }, []);

  // Uses LIVE wall-clock (`new Date()`) unless the debug "Set today's date & time" override is
  // set — NOT the module-level `TODAY`, which is frozen at page load. With the frozen value, a
  // forecast created during the session has createdAt > TODAY, so the 30-min REFORECAST cooldown
  // could never elapse without a reload.
  const effectiveToday = mockToday ? new Date(mockToday) : new Date();
  // The close turns the answer box and the trace cap on: keep the clock moving across it.
  useAnswerClock({ active, forecastTracking, mockToday, nowMs: effectiveToday.getTime() });
  // When viewing a PAST reforecast vintage (viewSnap present), the timeline rewinds its whole
  // clock to that vintage's moment, so a rewound view is a TRUE snapshot of what the timeline
  // showed then. Kept SEPARATE from `effectiveToday` so logic uses (the REFORECAST cooldown,
  // watchlist "… ago") stay on real wall-clock.
  //
  // The clock derives from the DISPLAYED snapshot's own moment (viewSnap.generatedAt), NOT the
  // selected viewMomentIso: during an async vintage load viewSnap still holds the PREVIOUS snapshot
  // while viewMomentIso has already advanced, so keying off the snapshot keeps the clock + label
  // consistent with the events actually on screen.
  const _vintageIso = viewSnap ? (viewSnap.generatedAt ?? viewMomentIso) : null;
  const _vintageMoment = _vintageIso ? new Date(_vintageIso) : null;
  const timelineToday =
    _vintageMoment && !isNaN(_vintageMoment.getTime()) ? _vintageMoment : effectiveToday;
  const todayDay = (timelineToday - R_START) / 86400000;
  const todayX = (todayDay - viewLeft) * ppd;
  // The gate is ZOOM-AWARE: a forecast made hours ago on the SAME day stays hidden while zoomed out
  // (its genX would overlap the today line) and appears the moment the user zooms in far enough for
  // the two lines to separate.
  const genCreatedAtDate = active?.createdAt ? new Date(active.createdAt) : null;
  const genDay =
    genCreatedAtDate && !isNaN(genCreatedAtDate.getTime())
      ? (genCreatedAtDate - R_START) / 86400000
      : null;
  const genX = genDay != null ? (genDay - viewLeft) * ppd : null;
  // This is a FLOOR, not a preference. Both lines are 2px wide, so anything smaller overlaps
  // outright instead of reading as two, and even this value only works because they differ in
  // colour (grey GENERATED, gold TODAY). Lowering it further would leave the marker unable to say
  // which line you are looking at.
  const MIN_GEN_GAP_PX = 2;
  const showGenLine =
    genDay != null && todayDay - genDay >= 1 / 24 && todayX - genX >= MIN_GEN_GAP_PX;
  // Pulls from the loaded log + the live `active` forecast, so a running root resolves before it
  // lands in the list.
  const compareRoots = useMemo(() => {
    const pool = active
      ? [
          active,
          ...forecasts.filter(
            (f) =>
              f.id !== active.id &&
              (active.forecastId == null || f.forecastId !== active.forecastId),
          ),
        ]
      : forecasts;
    // rootIds can hold both a root's client id (stored while its forecastId was still pending) AND
    // its forecastId, so dedupe by canonical forecast identity (forecastId when known, else client
    // id); otherwise one forecast could occupy two lanes.
    const seen = new Set();
    return comparison.rootIds
      .map((rid) => pool.find((f) => f.forecastId === rid || f.id === rid) || null)
      .filter((f) => {
        if (!f) return false;
        const key = f.forecastId || f.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [comparison, forecasts, active]);
  const compareOn = compareMode && compareRoots.length >= 2;
  // Owner-scoped key for the per-event render caches (slideCache, lastShownIds), which MUST be
  // keyed by the OWNING forecast/branch's globally-unique client id, not by bare ev.id: branch
  // events reuse the base forecast's id namespace (e1..eN), and in compare mode two roots' trunks
  // share it too, so bare-id keys collide and an event inherits a sibling's cached anchor. The
  // owner is read through refs mirroring active / compareRoots, so the (memoized) callers always
  // see current values regardless of which render captured this helper.
  const ownerActiveIdRef = useRef(null);
  ownerActiveIdRef.current = active?.id ?? null;
  const ownerCompareRootsRef = useRef(null);
  ownerCompareRootsRef.current = compareRoots;
  const rowOwnerKey = useCallback((rowId, evId) => {
    let owner;
    if (!rowId || rowId === "main") owner = ownerActiveIdRef.current || "main";
    else if (rowId.startsWith("compare-")) {
      const i = parseInt(rowId.slice(8), 10);
      owner = ownerCompareRootsRef.current?.[i]?.id || rowId;
    } else owner = rowId; // "branch-<id>" already carries the globally-unique branch id
    return `${owner}::${evId}`;
  }, []);
  const slideAnchorMs = (ev, rowId) => slideCache.current[rowOwnerKey(rowId, eventKey(ev))];
  const setSlideAnchorMs = (ev, rowId, ms) => {
    // eslint-disable-next-line react-hooks/immutability -- slideCache is AppInner's ref (reforecast bucket); writing its .current is the moved code's behaviour
    slideCache.current[rowOwnerKey(rowId, eventKey(ev))] = ms;
  };
  // The active forecast's branch run ids, refreshed each render once `branches` is known — how
  // eventAnchorMs tells the active lane's branch rows from a compared forecast's.
  const activeBranchIdsRef = useRef(new Set());
  // The resolution cap is the ACTIVE forecast's, so it bounds only its own lane and its
  // branches — a compared forecast (and its branches, which are "branch-<id>" rows too) can
  // resolve later.
  const ownsResolutionCap = (rowId) =>
    !rowId ||
    rowId === "main" ||
    (String(rowId).startsWith("branch-") &&
      activeBranchIdsRef.current.has(String(rowId).slice("branch-".length)));
  // The single source of truth for where an event's circle sits on the timeline (ms): server
  // mean_date if present, then the cached collision-slide position, then the range midpoint / the
  // event's own date. One source keeps the circle, its bell and any fork connector from diverging.
  // `uncapped` skips the resolution cap on an occurred trigger: the value the slide cache
  // stores, since a scrubbed pre-resolution vintage has no cap and must see the real date.
  const eventAnchorMs = (ev, rowId, { uncapped = false } = {}) => {
    const raw = (() => {
      if (ev.mean_date) {
        const m = new Date(ev.mean_date).getTime();
        if (!isNaN(m)) return m;
      }
      const cached = slideAnchorMs(ev, rowId);
      if (cached != null) return cached;
      // MIDDLE of the window for ANY range, not just a long one. The circle marks the most
      // likely moment, and with no server-provided mode the middle is the honest stand-in —
      // the expected value of a uniform window. Restricting this to >30-day ranges left a
      // short window (say the 10th–19th) with its circle pinned to the 10th while the band
      // ran to the 19th, so the marker and the distribution disagreed about one trigger.
      // A grading window is not a spread — a user-asserted event happens on `date`, and
      // `date_end` is only how long it stays gradable. Centring in it drifts the node right.
      const hasWindow = ev.date_end && ev.date_end !== ev.date && !ev._gradingWindow;
      return hasWindow
        ? (new Date(ev.date).getTime() + new Date(ev.date_end).getTime()) / 2
        : new Date(ev.date).getTime();
    })();
    const ownsCap = ownsResolutionCap(rowId);
    // Occurred triggers are capped at the resolved marker: nothing that happens after the question
    // settles belongs on its timeline.
    // An occurred trigger never sits right of the today line, whatever its window or cached slide.
    if (ev.isPast) {
      const pastMs = Math.min(raw, timelineToday.getTime());
      return !uncapped && _resCapMs != null && ownsCap ? Math.min(pastMs, _resCapMs) : pastMs;
    }
    // An unparseable date would make Math.max(NaN, …) silently poison layout, so fall back to
    // today.
    if (!Number.isFinite(raw)) return timelineToday.getTime();
    // Floor a future trigger to today, capped at the moment the resolution marker actually renders
    // (`_resCapMs`): resolved_at for an EARLY-resolved forecast (whose pie sits LEFT of the
    // horizon), else the horizon. `_resCapMs` is the SAME cap the marker + probability trace use;
    // it is null while unresolved, so the floor falls back to the horizon.
    const capMs =
      _resCapMs != null
        ? _resCapMs
        : (() => {
            const m = resolutionMomentLocal(active?.contract?.resolution_date, active?.question);
            return m && !isNaN(m.getTime()) ? m.getTime() : null;
          })();
    const floor =
      capMs != null ? Math.min(timelineToday.getTime(), capMs) : timelineToday.getTime();
    if (capMs == null || !ownsCap) return Math.max(raw, floor);
    // The server can hand a trigger past the question's resolution a window (or a mean_date) that
    // runs beyond it. A window that straddles the resolution is centred in the part of it that
    // still matters; the anchor is then capped at the marker.
    let anchored = raw;
    if (!ev.mean_date && slideAnchorMs(ev, rowId) == null) {
      const start = new Date(ev.date).getTime();
      const end = ev.date_end ? new Date(ev.date_end).getTime() : NaN;
      const hasWindow = ev.date_end && ev.date_end !== ev.date && !ev._gradingWindow;
      if (hasWindow && Number.isFinite(start) && Number.isFinite(end) && end > capMs)
        anchored = start < capMs ? (start + capMs) / 2 : capMs;
    }
    return Math.min(Math.max(anchored, floor), capMs);
  };
  // Mirrors compareRoots order, so the log row's outline color matches its timeline lane.
  const compareLaneOf = useCallback(
    (f) =>
      compareMode
        ? compareRoots.findIndex(
            (r) => r.id === f.id || (r.forecastId != null && r.forecastId === f.forecastId),
          )
        : -1,
    [compareMode, compareRoots],
  );
  const timelineEnd = useMemo(() => {
    const rd = active?.contract?.resolution_date ? new Date(active.contract.resolution_date) : null;
    if (!rd || isNaN(rd.getTime())) return R_END;
    const ext = new Date(rd);
    ext.setFullYear(ext.getFullYear() + 10);
    return ext > R_END ? ext : R_END;
  }, [active]);
  // Coarse scale (week / month / quarter / year / decade): pan-INDEPENDENT
  // (getTicks walks the whole range and visTicks filters), so this memo must NOT
  // depend on viewLeft. Skipped entirely once we're zoomed into fine (day/hour)
  // territory (minGap ≤ 1) — getFineTicks owns that band.
  const ticks = useMemo(() => {
    const minGap = cSize.w / ppd / 20;
    return minGap <= 1 ? [] : getTicks(ppd, cSize.w, timelineEnd);
  }, [ppd, cSize.w, timelineEnd]);
  // Fine scale (each day → each hour) for short-horizon zooms. getFineTicks is
  // viewport-BOUNDED, so this memo CAN depend on viewLeft (it's cheap — a few
  // dozen ticks) and must, since the window moves as you pan.
  const fineTicks = useMemo(() => {
    const minGap = cSize.w / ppd / 20;
    return minGap <= 1 ? getFineTicks(ppd, viewLeft, cSize.w, timelineEnd) : null;
  }, [ppd, cSize.w, timelineEnd, viewLeft]);
  const activeTicks = fineTicks || ticks;
  // Branches are ordered top→bottom by fork date (LATEST first — a later fork sits higher) to
  // reduce connector overlap. Both this list (layout via perLaneBranches) and the render list
  // (laneRuns) go through orderBranchesByFork so their branchYs[i]↔branch[i] indexing stays
  // aligned.
  //
  // VISIBLE branches: an acknowledged-and-cleared branch is archived, not deleted, so it stays in
  // the store (scoreable, and renderable when rewinding to an earlier vintage) but drops out of the
  // live lane. At a past mark, the branches that existed THEN (created by that moment, not yet
  // cleared); live, every uncleared branch. The same list feeds the lane geometry and the lane
  // render.
  const branchesAtMs = viewSnap ? Date.parse(viewSnap.generatedAt ?? viewMomentIso) : null;
  const branchesAt = Number.isFinite(branchesAtMs) ? branchesAtMs : null;
  // Memoized: laneRuns keys on it, and AppInner re-renders on every hover.
  const branches = useMemo(
    () => orderBranchesByFork(selectVisibleBranchRuns(runState, active?.id, branchesAt)),
    [runState, active?.id, branchesAt],
  );
  const { offset: questionClearance, resume: resumeQuestionClearance } = useQuestionClearance({
    enabled: true,
    scrollRef: evtScrollRef,
    activeId: active?.id ?? null,
  });
  // Branches lay out below the spine and grow the scroll container, so the user can scroll as
  // branches come in.
  // The lane fills the column under the header, so the spine sits just above its middle,
  // leaving room above and below for the distribution at the resolution date.
  // What sits at the foot of the timeline column, which every bottom-anchored overlay clears: the
  // signal bar.
  const signpostsH = active ? SIGNAL_BAR_H : 0;
  const SPINE_Y =
    Math.max(
      LANE_SPINE_Y,
      Math.round(((cSize.h || 0) - forecastHeaderH - AXIS_H - signpostsH) * 0.42),
    ) +
    railTimelineDrop +
    questionClearance;
  // The lane palette, returned for renderTimelineContent's lane map (lane 0 keeps themeRaw.purple
  // there; lanes 1+ index into this) and for useLaneCtx's colors. The v1 limit is 2 lanes.
  const LANE_COLORS = [themeRaw.purple, themeRaw.green];
  // Lanes cannot sit at fixed SPINE_Y+i*BRANCH_SPACING offsets, because each lane needs vertical
  // room for its branch stack before the next lane's spine. In single-forecast mode there is
  // exactly one lane carrying `branches` (= active's), so lanes[0].branchYs reproduces the old
  // single-stack layout byte-for-byte and lanes[0].spineY === SPINE_Y — the regression guard (see
  // stackedLaneLayout doc).
  const laneGeom = useMemo(() => {
    // stackedLaneLayout reads only b.collapsed and b.forkEvent per branch, both present on run
    // objects.
    const perLaneBranches = compareOn
      ? compareRoots.map((r) => orderBranchesByFork(selectVisibleBranchRuns(runState, r?.id)))
      : [branches];
    return stackedLaneLayout(perLaneBranches, SPINE_Y, BRANCH_SPACING);
  }, [runState, compareOn, compareRoots, SPINE_Y, branches]);
  // totalHeight spans ALL lanes, so the timeline scroll box fits every lane's branch stack.
  const branchLayout = { totalHeight: laneGeom.totalHeight };
  activeBranchIdsRef.current = new Set(branches.map((b) => b.runId));

  const { laneRuns, liveProjectionToday, premiseViews } = useLaneRuns({
    active,
    applyAlternateSpace,
    branches,
    branchesAt,
    compareOn,
    debugPremiseOverride,
    forecastLiveProgressEnabled,
    runState,
    timelineToday,
    viewMomentIso,
    viewSnap,
  });

  const resDate = active?.contract?.resolution_date;
  // The forecast line sits at the resolution DISPLAY MOMENT (resolutionMomentLocal). A date-only
  // resolution lands at the END of that day (next local midnight), so a resolution of *today*
  // renders to the RIGHT of the live today line; a "before <date>" question lands at the START of
  // the date; an explicit time is honored as-is.
  const resScreenX = resDate
    ? (() => {
        const d = resolutionMomentLocal(resDate, active?.question);
        // Invalid resolution_date → null (not dayToScreen(NaN)); consumers already
        // guard null / Number.isFinite, but a NaN would leak into layout math.
        if (!d || isNaN(d.getTime())) return null;
        return dayToScreen(d);
      })()
    : null;
  const RES_SZ = BASE_R * 2 + 6;
  // When the resolver settles the question BEFORE the forecast horizon, the verdict pie/glyph/label
  // render at the ACTUAL resolution date (resolvedScreenX), taken from the resolver verdict's
  // resolved_at, while the purple forecast line stays put at resScreenX (relabelled "ORIGINALLY
  // FORECASTED"). In debug we synthesize a midpoint off the FORCED verdict so the whole
  // early-resolve visual is exercisable from Settings.
  //
  // Scrub-vs-verdict precedence is driven by entry kind: a verdict renders unless the user has
  // scrubbed to a FORECAST vintage (whose moment predates resolution, so it must show its own
  // historical probability). At rest, or scrubbed to a resolution entry, the verdict applies. The
  // verdict reflects the SELECTED resolution attempt (currentResolutionEntry), not just the newest,
  // so scrubbing to an older attempt shows ITS status. The debug override (Force Resolution) still
  // wins for the no-real-resolution test path.
  const scrubbedToForecast = !compareOn && !!viewMomentIso && !viewEntry?.isResolution;
  const _resVerdictStatus = scrubbedToForecast
    ? null
    : debugResolutionOverride
      ? debugResolutionOverride
      : currentResolutionEntry
        ? (currentResolutionEntry.status ?? null)
        : forecastTracking
          ? (active?.resolution?.status ?? null)
          : null;
  // A MULTI-VALUE contract settles by NAMING AN OUTCOME and carries no status token — the
  // yes/no/tie vocabulary cannot say which of K outcomes occurred, so the notifier
  // persists `resolved_outcome.outcome_index` and no status (see multiway.js
  // `resolvedBucketIndex`). Read from the same source the status is read from, so
  // scrubbing to an older attempt keeps showing THAT attempt's verdict rather than the
  // newest one leaking through.
  const _resNamesOutcome = scrubbedToForecast
    ? false
    : isNamedOutcomeVerdict(
        currentResolutionEntry
          ? // `snapshot` is the entry's inline copy of server_ctx.resolution
            // (reforecastEntries reconciles it onto the matching pointer). A BARE
            // pointer carries no snapshot and names no slot — correct, since the
            // pointer path has only ever carried a status, never the outcome.
            currentResolutionEntry.snapshot
          : forecastTracking
            ? active?.resolution
            : null,
      );
  const _resHasTerminal =
    _resVerdictStatus === "resolved_yes" ||
    _resVerdictStatus === "resolved_no" ||
    _resVerdictStatus === "resolved_tie" ||
    _resNamesOutcome;
  const resolvedAtIso =
    (currentResolutionEntry
      ? currentResolutionEntry.moment
      : forecastTracking
        ? active?.resolution?.resolved_at
        : null) ||
    (() => {
      if (!(debugResolutionOverride && _resHasTerminal && resScreenX != null && resDate))
        return null;
      const _rd = resolutionMomentLocal(resDate, active?.question);
      if (!_rd || isNaN(_rd.getTime())) return null;
      // Full ISO timestamp (NOT date-only): the synth is a precise midpoint instant;
      // a YYYY-MM-DD string would parse as UTC midnight → day-shift.
      return new Date((effectiveToday.getTime() + _rd.getTime()) / 2).toISOString();
    })();
  // parseResolveDateLocal (not new Date): a date-only `resolved_at` from the
  // server is read as LOCAL midnight, so the verdict geometry can't slip a day in
  // non-UTC timezones (a full ISO timestamp is still parsed as the instant).
  const resolvedScreenX = resolvedAtIso
    ? (() => {
        const d = parseResolveDateLocal(resolvedAtIso);
        if (!d || isNaN(d.getTime())) return null;
        return dayToScreen(d);
      })()
    : null;
  const earlyResolved =
    _resHasTerminal &&
    resolvedScreenX != null &&
    resScreenX != null &&
    resolvedScreenX < resScreenX - 1;
  // Cap the probability trace at the SAME moment the resolution marker renders, using the same
  // `earlyResolved` gate, so a resolved forecast's past line stops at the marker instead of drawing
  // a flat segment out to today, to the RIGHT of the resolution pie. Early resolve: the pie moves
  // LEFT to resolved_at (resolvedScreenX), so cap at resolvedAtIso. On-time or LATE resolve: the
  // marker stays at the horizon (resScreenX), so cap at the horizon line; a LATE resolved_at sits
  // to the RIGHT of the horizon pie and must not be used. Null while unresolved (no cap).
  const _resCapMs = _resHasTerminal
    ? (() => {
        const d =
          earlyResolved && resolvedAtIso
            ? parseResolveDateLocal(resolvedAtIso)
            : resDate
              ? resolutionMomentLocal(resDate, active?.question)
              : null;
        return d && !isNaN(d.getTime()) ? d.getTime() : null;
      })()
    : null;
  // A closed question still waiting for its answer: the trace ends at the close, never runs on to
  // today. Not a verdict, so it caps the plot only (no resolved mark, no trigger floor).
  const _awaitCapMs =
    _resCapMs == null &&
    forecastTracking &&
    !viewSnap &&
    awaitingAnswer(active, effectiveToday.getTime())
      ? closeMs(active)
      : null;
  // A settled forecast ends on a gold "Resolved: <outcome>" mark where the trace
  // stops, in place of the violet resolution-date line (lib/timeline/resolvedOutcome.js).
  const resolvedMark = resolvedMarkOf({
    capMs: _resCapMs,
    dayToScreen,
    contract: displayActive?.contract,
    allMultiway: allMultiwayEnabled,
    status: _resVerdictStatus,
    verdict: currentResolutionEntry?.snapshot || (forecastTracking ? active?.resolution : null),
  });
  // Nothing happens after a settled forecast's Resolved mark, so the TODAY rule beyond it marks
  // nothing. Scrubbed to a vintage from before it settled there is no mark, and the rule is back
  // as the moment being viewed.
  const showTodayLine = !resolvedMark;
  // computeVisibility is memoized on zoom alone, so it reads these through a ref — a
  // closure would keep the anchor/cap from before the resolution landed.
  const liveAnchorRef = useRef(null);
  liveAnchorRef.current = { eventAnchorMs, resCapMs: _resCapMs, todayMs: timelineToday.getTime() };
  // Cached anchors and slide positions are chosen under ONE resolution cap. Scrubbing between
  // a resolved view and a pre-resolution vintage changes the cap, and a position picked under
  // the marker would pin a trigger there in a view that has no marker — so drop them all
  // (the same reset a forecast switch does) and let the next pass choose afresh.
  const anchorCapRef = useRef(_resCapMs);
  if (anchorCapRef.current !== _resCapMs) {
    anchorCapRef.current = _resCapMs;
    // eslint-disable-next-line react-hooks/immutability -- slideCache is AppInner's ref (reforecast bucket); writing its .current is the moved code's behaviour
    slideCache.current = {};
  }
  const TEN_YEARS_DAYS = 3653;
  // The fade anchor uses the same display moment as resScreenX (end-of-day for date-only).
  const _fadeResDate = resDate ? resolutionMomentLocal(resDate, active?.question) : null;
  // The canvas (future tint, grid, axis dates) fades out just past the
  // resolution line, so the lane reads as today → resolution instead of running on for years
  // under the outcome bars.
  const _fadeAtRes = !!_fadeResDate && !isNaN(_fadeResDate.getTime());
  const fadeAnchorDay = _fadeAtRes
    ? (_fadeResDate - R_START) / 86400000
    : todayDay + TEN_YEARS_DAYS;
  const fadeStartX = (fadeAnchorDay - viewLeft) * ppd + (_fadeAtRes ? 24 : 0);
  // Fade transition is wider than viewport so it feels gradual at any zoom
  const FADE_PX = _fadeAtRes ? 140 : Math.max(420, (cSize.w || 800) * 0.5);
  const fadeEndX = fadeStartX + FADE_PX;
  const tickOpacity = (sx) => {
    if (sx <= fadeStartX) return 1;
    if (sx >= fadeEndX) return 0;
    const t = (sx - fadeStartX) / FADE_PX;
    return 1 - t * t * (3 - 2 * t);
  };

  const formatCrossDate = useCallback(
    (sx) => {
      const day = screenToDay(sx);
      const d = new Date(R_START.getTime() + day * 86400000);
      const dd = d.getDate();
      const suf =
        dd === 1 || dd === 21 || dd === 31
          ? "st"
          : dd === 2 || dd === 22
            ? "nd"
            : dd === 3 || dd === 23
              ? "rd"
              : "th";
      const mon = d.toLocaleString("default", { month: "short" });
      const yr = String(d.getFullYear()).slice(2);
      return `${dd}${suf}, ${mon}. '${yr}`;
    },
    [screenToDay],
  );

  useEffect(() => {
    if (!containerReady) return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect.width,
        h = e.contentRect.height;
      setCSize({ w, h });
      if (w > 0) setMeasured(true);
    });
    ro.observe(el);
    // The observer's size (the content box, fractional), not offsetWidth's rounded one: the
    // view init fires once, on whichever measurement lands first, and a rounded width moved the
    // timeline a fraction of a pixel from one load to the next.
    const cs = getComputedStyle(el);
    const w0 = parseFloat(cs.width) || 0,
      h0 = parseFloat(cs.height) || 0;
    setCSize({ w: w0, h: h0 });
    if (w0 > 0) setMeasured(true);
    return () => ro.disconnect();
  }, [containerReady]);

  const [promotedIds, setPromotedIds] = useState(new Set());
  const [collapsedIds, setCollapsedIds] = useState(new Set());
  const visTicks = useMemo(
    () =>
      activeTicks.filter((t) => {
        const sx = (t.x / ppd - viewLeft) * ppd;
        return sx > -50 && sx < cSize.w + 50;
      }),
    [activeTicks, ppd, viewLeft, cSize.w],
  );

  const vw = cSize.w || 800;
  return {
    FADE_PX,
    LANE_COLORS,
    MIN_GEN_GAP_PX,
    RES_SZ,
    SPINE_Y,
    TEN_YEARS_DAYS,
    _fadeAtRes,
    _fadeResDate,
    _answerBoxOn: !!forecastTracking,
    _awaitCapMs,
    _resCapMs,
    _resHasTerminal,
    _resNamesOutcome,
    _resVerdictStatus,
    _vintageIso,
    _vintageMoment,
    activeBranchIdsRef,
    activeTicks,
    anchorCapRef,
    animRef,
    autoZoomRef,
    axisHoverX,
    branchLayout,
    branches,
    branchesAt,
    branchesAtMs,
    cSize,
    cSizeRef,
    collapsedIds,
    compareLaneOf,
    compareOn,
    compareRoots,
    containerReady,
    containerRef,
    crossX,
    dayToScreen,
    dragRef,
    earlyResolved,
    effectiveToday,
    eventAnchorMs,
    evtScrollRef,
    fadeAnchorDay,
    fadeEndX,
    fadeStartX,
    fineTicks,
    formatCrossDate,
    genCreatedAtDate,
    genDay,
    genX,
    laneGeom,
    laneRuns,
    liveAnchorRef,
    liveProjectionToday,
    measured,
    ownerActiveIdRef,
    ownerCompareRootsRef,
    ownsResolutionCap,
    panAnimRef,
    ppd,
    ppdRef,
    premiseViews,
    promotedIds,
    questionClearance,
    rafRef,
    resDate,
    resScreenX,
    resolvedAtIso,
    resolvedMark,
    resolvedScreenX,
    resumeQuestionClearance,
    rowOwnerKey,
    scheduleRender,
    screenToDay,
    scrubbedToForecast,
    selEvt,
    selRect,
    selectRef,
    setAxisHoverX,
    setCSize,
    setCollapsedIds,
    setContainerReady,
    setContainerRef,
    setCrossX,
    setMeasured,
    setPromotedIds,
    setSelEvt,
    setSelRect,
    setSlideAnchorMs,
    setTick,
    showGenLine,
    showTodayLine,
    signpostsH,
    slideAnchorMs,
    tickOpacity,
    ticks,
    timelineEnd,
    timelineToday,
    todayDay,
    todayX,
    vPanAnimRef,
    viewLeft,
    visTicks,
    vlRef,
    vw,
  };
}

/**
 * Framing: the wheel zoom/pan listener, the right-hand buffer for the resolution marker, the lifespan
 * frame of a resolved forecast, useTimelineZoom and the effects that re-frame on selection, on a
 * compare leg settling and after a panel transition.
 */
export function useTimelineFraming({
  geom,
  active,
  allMultiwayEnabled,
  applyAlternateSpace,
  authLoading,
  chatOpen,
  logOpen,
  railPanelOpen,
  railPanelWidth,
  runState,
}) {
  const {
    _answerBoxOn,
    _awaitCapMs,
    _resHasTerminal,
    animRef,
    autoZoomRef,
    branches,
    cSize,
    cSizeRef,
    compareOn,
    containerReady,
    containerRef,
    dayToScreen,
    effectiveToday,
    evtScrollRef,
    laneGeom,
    panAnimRef,
    ppdRef,
    resDate,
    resolvedAtIso,
    scheduleRender,
    setCollapsedIds,
    setPromotedIds,
    todayDay,
    vPanAnimRef,
    vlRef,
  } = geom;

  // Depends on `containerReady`, NOT just `scheduleRender`: `authLoading` is true on the first
  // commit, so AppInner returns the shell and the timeline container isn't mounted yet. An effect
  // keyed only on the stable `scheduleRender` would run once with `containerRef.current===null`,
  // bail, and never re-attach when the container mounts, so pinch would fall through to native
  // page-zoom. `containerReady` flips via the callback ref when the element mounts, so the effect
  // re-runs and binds then.
  useEffect(() => {
    if (!containerReady) return;
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      // ctrl/⌘ + wheel (trackpad pinch) → zoom the timeline scale.
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const cursorX = clientToLocalX(el, e.clientX).x;
        const oldPpd = ppdRef.current;
        const dayAtCursor = vlRef.current + cursorX / oldPpd;
        const factor = Math.pow(2, -e.deltaY * 0.003);
        const newPpd = Math.max(0.08, Math.min(3000, oldPpd * factor));
        vlRef.current = dayAtCursor - cursorX / newPpd;
        ppdRef.current = newPpd;
        if (newPpd !== oldPpd) {
          setPromotedIds(new Set());
          setCollapsedIds(new Set());
        }
        scheduleRender();
        return;
      }
      // Claim only clearly horizontal gestures (|deltaX| > |deltaY|); vertical-dominant ones fall
      // through to native scroll on the evt-scroll container, so a two-finger swipe moves BOTH
      // axes. deltaX is CSS px; divide by ppd (px/day) like the drag pan.
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        vlRef.current += e.deltaX / ppdRef.current;
        scheduleRender();
      }
    };
    // telemetry-gesture: wheel zoom and pan of the timeline (view navigation)
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [scheduleRender, containerReady]);
  // Space the framing must leave to the RIGHT of the forecast line, so the marker stays clear of
  // the chat console. Measured from the marker's own tokens, so it tracks the bars rather than
  // restating their size here.
  const resolutionRightBuffer = (() => {
    // See resolutionRightBufferFor: `assumeMarker` stays true while the flags are loading
    // so a restored forecast is framed for the marker rather than the narrow % label.
    const assumeMarker = authLoading || allMultiwayEnabled;
    const dist = extractDistribution((compareOn ? active : applyAlternateSpace(active))?.contract, {
      allMultiway: true,
      probability: active?.contract?.probability,
    });
    // Reserve the settled tag ONLY once the forecast has actually settled: always reserving it
    // wastes space between the last label and the console, and never reserving it tucks "✓
    // RESOLVED" under the console the moment a forecast resolves. The caller is the one place
    // that knows which state it is in.
    // A closed question still waiting for its answer also holds the answer box past the marker.
    return (
      resolutionRightBufferFor(dist?.buckets ?? null, {
        assumeMarker,
        badge: _resHasTerminal ? "Resolved" : undefined,
      }) + (_awaitCapMs != null ? ANSWER_BOX_GAP + ANSWER_BOX_W : 0)
    );
  })();

  // The same reservation for an ARBITRARY forecast — used when framing one that has not
  // landed in `active` yet (boot restore, picking a forecast from the log). Without it those
  // paths framed against the active forecast's buffer and opened zoomed in past CENTER.
  // `run`, when given, adds the answer box a closed question still waiting for its answer shows.
  const bufferForContract = (contract, run = null) =>
    resolutionRightBufferFor(
      extractDistribution(contract, {
        allMultiway: true,
        probability: contract?.probability,
      })?.buckets ?? null,
      // No verdict in hand for an arbitrary contract, so RESERVE the tag: this frames a
      // forecast being switched to, where over-reserving costs a little right margin for one
      // paint and under-reserving clips the tag until the user re-centres.
      { assumeMarker: authLoading || allMultiwayEnabled, badge: "Resolved" },
    ) +
    (_answerBoxOn && run && awaitingAnswer(run, effectiveToday.getTime())
      ? ANSWER_BOX_GAP + ANSWER_BOX_W
      : 0);
  // The LIFESPAN frame for a resolved forecast (see lib/forecast/resolvedFrame.js); null
  // while live, which frames exactly as before. Fed the SAME verdict + resolved_at the
  // marker reads, so a scrubbed vintage frames to its own attempt, and shared by BOTH
  // framing paths below — the on-selection effect and CENTER — since either one left on
  // the live rule hands a resolved row straight back to the wrong zoom.
  const activeResolvedFrame = useMemo(
    () =>
      resolvedFrameTarget({
        hasTerminalVerdict: _resHasTerminal,
        resolvedAtIso,
        resolutionDate: resDate,
        question: active?.question,
        createdAt: active?.createdAt,
      }),
    [_resHasTerminal, resolvedAtIso, resDate, active?.question, active?.createdAt],
  );
  const { jumpTo, centerToday, autoZoom } = useTimelineZoom({
    cSize,
    resolutionRightBuffer,
    // The rail layout frames with 20% more room right of the bin labels.
    // No extra pad: the default frame is today → resolution with an 8% margin.
    labelPadScale: 1,
    resolvedFrame: activeResolvedFrame,
    cSizeRef,
    vlRef,
    ppdRef,
    animRef,
    scheduleRender,
    active,
    // The rail's one panel sits on the LEFT, so it frames like the Forecast Manager does.
    chatOpen: false,
    logOpen: railPanelOpen,
    logWidth: railPanelWidth,
    todayDay,
    setPromotedIds,
    setCollapsedIds,
  });
  // Keep autoZoomRef pointing at the latest autoZoom. Assigned during RENDER
  // (not in an effect) — like cSizeRef.current=cSize above — so it's populated
  // before ANY effect runs. The mount resume effect calls reattachToForecast →
  // autoZoomRef.current(); a useEffect-based sync runs in declaration order and
  // could fire AFTER that on the initial reload-resume, leaving the ref null and
  // making the reframe a silent no-op. Render-time assignment closes that window.
  autoZoomRef.current = autoZoom;

  // Whatever selects a forecast, the timeline gets framed. This effect owns the invariant in one
  // place: when the ACTIVE forecast changes, frame it exactly the way CENTER would. It is
  // path-independent, so a new selection route cannot forget it.
  //
  // It is safe to leave running for two reasons. First, it fires once per forecast id,
  // resolved-ness and marker width (the key below) — all data, never view state — so panning or
  // zooming afterwards does not re-trigger it, and it can
  // never fight a deliberate gesture. Second, `cSize.w` is a dependency: a path that frames during
  // boot measures the 800px placeholder container, so this re-frames once the real width arrives,
  // then stops.
  const framedForRef = useRef(null);
  // The boolean, not the memo object: `activeResolvedFrame`'s identity churns on unrelated
  // renders, and only the live→resolved transition should re-enter the framing effect.
  // Hoisted because eslint cannot statically check a complex expression in a dep array.
  const activeIsResolved = !!activeResolvedFrame;
  // The room the marker needs: framed while the run is still preprocessing it is sized for
  // the binary Yes/No labels, so the key below re-frames once the real bins land.
  const activeFrameBuffer = active?.contract
    ? Math.round(bufferForContract(active.contract, active))
    : 0;
  useEffect(() => {
    const id = active?.id ?? null;
    const rd = active?.contract?.resolution_date;
    // Wait for a measured container: framing against the placeholder width is the thing
    // this exists to correct, so doing it here too would just re-introduce it.
    if (!id || !rd || !cSize.w) return;
    // Keyed on resolved-ness as well as id, so a forecast that SETTLES while open reframes exactly
    // once more: the live frame runs out to today (keepTodayInFrame below) while the resolved one
    // stops at the lifespan cap. A key, not a bare dep: it still fires once per STATE, never on a
    // pan or a clock tick.
    const frameKey = `${id}:${activeResolvedFrame ? "resolved" : "live"}:${activeFrameBuffer}`;
    if (framedForRef.current === frameKey) return;
    framedForRef.current = frameKey;
    autoZoomRef.current?.(
      activeResolvedFrame?.target ?? rd,
      true,
      // Falls back to the forecast's CREATION, not null, so a live forecast frames its whole
      // lifespan the way a resolved one already does (`activeResolvedFrame` is null while live).
      // autoZoom clamps with Math.min(todayDay, startMs), so a forecast created today is
      // unaffected.
      activeResolvedFrame?.startDate ?? active?.createdAt ?? null,
      bufferForContract(active.contract, active),
      // keepTodayInFrame only on the LIVE branch (`activeResolvedFrame` null). A frame
      // that ends at the resolution date drops the today cluster once today runs past
      // it, and the ↻ REFORECAST pill renders only while `todayX` is on screen — so an
      // unresolved forecast past its date lost the one button that could re-run it. A
      // RESOLVED forecast keeps framing its lifespan: one that settled months ago would
      // squash that history into a sliver if stretched to now.
      !activeResolvedFrame,
    );
    // `bufferForContract` and autoZoomRef are read live and deliberately unlisted: the
    // former is a plain function recreated each render, and listing it (or autoZoom itself,
    // whose identity changes when a drawer toggles) would re-fire this on every render and
    // stomp the user's framing. `activeIsResolved` carries the live→resolved transition.
    // Stays `-next-line` (a compiler opt-out of useTimelineFraming, counted by
    // `featuresCompilerOptOuts`) until the render-time `autoZoomRef.current = autoZoom` above is
    // moved out of render: converting it surfaces that line to `react-hooks/refs` (PKG-05c, F-40).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, active?.contract?.resolution_date, cSize.w, activeIsResolved, activeFrameBuffer]);

  // Compare-mode refit on EITHER leg completing. The legs can settle at different times
  // (separate runs, separate streams), and postSubmit fits the x-scale to the PRIMARY's
  // resolution only. So when a SECONDARY leg completes and gains its own resolution_date, this
  // effect fits to the UNION of ALL comparison lanes' date ranges (unionDateRange → {min,max}),
  // zooming to the latest resolution (union.max). The dependency signal below carries every
  // lane's resolution_date, so a secondary runCompleted (null → date) re-fires it.
  // animate=false: a data-driven reframe, not a user gesture. Scoped to the compare case only
  // (the `if(!compareOn)return` bail).
  const compareResSignal = useMemo(() => {
    if (!compareOn) return "";
    return selectComparisonLanes(runState)
      .map((r) => `${r.runId}:${r.contract?.resolution_date || ""}`)
      .join("|");
  }, [compareOn, runState]);
  useEffect(() => {
    if (!compareOn) return;
    const fitRange = unionDateRange(selectComparisonLanes(runState));
    // Call via autoZoomRef (render-synced to the latest autoZoom) rather than the
    // autoZoom callback itself: autoZoom's identity changes whenever chatOpen/logOpen
    // toggle (they're in its deps for the panel-width math), so listing autoZoom as a
    // dep here would re-fire this refit on every drawer toggle and reset the user's
    // compare framing. The ref keeps the effect firing only on the intended signals.
    if (fitRange) autoZoomRef.current?.(fitRange.max.toISOString().slice(0, 10), false);
    // compareResSignal is the change trigger (a leg gaining a resolution_date);
    // runState is read live inside but intentionally NOT a dep (it churns on
    // every store action — we only want to refit when a lane's resolution
    // date set changes, captured by compareResSignal).
  }, [compareOn, compareResSignal, cSize.w]); // eslint-disable-line react-hooks/exhaustive-deps -- runState is read live; compareResSignal is the trigger (above)

  // Re-render timeline after panel open/close transition so the revealed area repaints
  useEffect(() => {
    const id1 = setTimeout(scheduleRender, 0);
    const id2 = setTimeout(scheduleRender, 320);
    return () => {
      clearTimeout(id1);
      clearTimeout(id2);
    };
  }, [chatOpen, logOpen, scheduleRender]);

  // Extracted to a callback so the chat-intent hook doesn't reach into timeline render internals
  // (ppdRef/vlRef/scheduleRender).
  const fitTimeline = useCallback(() => {
    if (active?.contract?.resolution_date) {
      const resD = new Date(active.contract.resolution_date);
      const totalDays = Math.max(30, (resD - TODAY) / 86400000 + 60);
      const targetPpd = Math.max(0.08, (cSize.w || 800) / totalDays);
      ppdRef.current = targetPpd;
      vlRef.current = (TODAY - R_START) / 86400000 - 30;
      scheduleRender();
    } else jumpTo(SCALE_PRESETS[0].ppd);
  }, [active, cSize.w, scheduleRender, jumpTo]);

  // A `?forecast=` link to one of the active forecast's branches: scroll its lane into view, pan
  // to its fork date, and glow it briefly (SOO-1008). It waits for the framing animation that
  // opening the root starts, and measures against the lane the side panel leaves visible (as
  // useComputeVisibility does). Single-forecast mode only: in compare mode lanes[0] is not
  // necessarily `branches`' lane.
  const panBy = useCallback(
    (dx) => {
      vlRef.current += dx / ppdRef.current;
      scheduleRender();
    },
    [vlRef, ppdRef, scheduleRender],
  );
  const isAnimating = useCallback(
    () => Boolean(animRef.current || panAnimRef.current || vPanAnimRef.current),
    [animRef, panAnimRef, vPanAnimRef],
  );
  // The lane draws a fork no later than the root's resolution line (ForecastLane's clamp).
  const branchLinkResMoment = resolutionMomentLocal(
    active?.contract?.resolution_date,
    active?.question,
  );
  const branchLinkForkCapMs =
    branchLinkResMoment && !isNaN(branchLinkResMoment.getTime())
      ? branchLinkResMoment.getTime()
      : Infinity;
  // A lean-row boot draws "Loading forecast…" until the root's detail lands (or its read gives
  // up), and that landing reframes the view: wait for it, as the lane does (awaitsDetail).
  const detailFailed = useSyncExternalStore(
    subscribeDetailFailed,
    getDetailFailed,
    getDetailFailed,
  );
  const branchLinkRoot = active?.id ? selectRun(runState, active.id) : null;
  const branchLinkRootReady =
    Boolean(branchLinkRoot) && !awaitsDetail(branchLinkRoot, detailFailed);
  const branchLinkGlowId = useBranchLinkFocus({
    focus: runState?.branchLinkFocus,
    branches: branches ?? [],
    branchYs: compareOn ? null : laneGeom?.lanes?.[0]?.branchYs,
    scrollRef: evtScrollRef,
    dayToScreen,
    laneLeft: railPanelOpen ? railPanelWidth : 0,
    laneRight: cSize.w || 800,
    panBy,
    isAnimating,
    ready: containerReady && branchLinkRootReady,
    forkCapMs: branchLinkForkCapMs,
  });

  return {
    activeFrameBuffer,
    activeIsResolved,
    activeResolvedFrame,
    autoZoom,
    branchLinkGlowId,
    bufferForContract,
    centerToday,
    compareResSignal,
    fitTimeline,
    framedForRef,
    jumpTo,
    resolutionRightBuffer,
  };
}
